import { clearOwnAi } from './ownAi';
/**
 * The account, as one piece of state the screens can read.
 *
 * The same loading shape the library uses for its catalogue, and for the same reason: an effect
 * owns the reading, everything else asks for a re-read by bumping a token. A `refresh` that any
 * handler could `await` would set state after a network round trip with nothing to say whether
 * the component is still mounted, and would give one job two entry points that interleave.
 *
 * `loading` is a state and not a boolean beside the others, because the difference between "we
 * have not asked yet" and "there is no account layer here" is the difference between showing
 * nothing for a moment and showing nothing for ever. Both render as nothing; only one of them
 * ever changes.
 */
import {
  createContext,
  createElement,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { activateStorageAccount, storageAccount, whenStorageIsIdle } from '../engine/storage';

import { count } from '../telemetry/telemetry';

import { readAccount, signOut, type AccountState } from './api';

export type AccountView =
  { readonly status: 'loading' } | { readonly status: 'storage-error' } | AccountState;

export interface Account {
  readonly state: AccountView;
  /** Ends the session and re-reads. The re-read is what puts the sign-in controls back. */
  readonly endSession: (scope: 'this-device' | 'everywhere') => void;
  readonly refresh: () => void;
}

const AccountContext = createContext<Account | null>(null);

export function AccountProvider({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const account = useAccountState();
  if (account.state.status === 'loading')
    return createElement('p', { role: 'status' }, 'Opening your library…');
  if (account.state.status === 'storage-error')
    return createElement(
      'div',
      { role: 'alert' },
      'Your library could not be opened. Your existing data was kept.',
      createElement('button', { onClick: account.refresh }, 'Try again'),
    );
  return createElement(
    AccountContext.Provider,
    { value: account, key: storageAccount() ?? 'guest' },
    children,
  );
}

export function useAccount(): Account {
  const account = useContext(AccountContext);
  if (account === null) throw new Error('useAccount outside AccountProvider');
  return account;
}

function useAccountState(): Account {
  const [state, setState] = useState<AccountView>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const countedSignIn = useRef(false);

  const refresh = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    const activity = { cancelled: false };
    const isCancelled = (): boolean => activity.cancelled;

    const load = async (): Promise<void> => {
      const next = await readAccount();
      if (isCancelled()) return;
      let userId = next.status === 'signed-in' ? next.user.id : null;
      if (next.status === 'unavailable') {
        try {
          userId = localStorage.getItem('orbit-mind.active-account');
        } catch {
          /* device-only */
        }
      }
      try {
        if (userId !== storageAccount()) setState({ status: 'loading' });
        await whenStorageIsIdle();
        if (isCancelled()) return;
        await activateStorageAccount(userId);
        if (isCancelled()) return;
        try {
          if (userId === null) localStorage.removeItem('orbit-mind.active-account');
          else localStorage.setItem('orbit-mind.active-account', userId);
        } catch {
          /* The active session still has its own namespace. */
        }
      } catch {
        if (!isCancelled()) setState({ status: 'storage-error' });
        return;
      }

      /*
       * The funnel step, counted once.
       *
       * A ref rather than a comparison against `state`: reading state inside an effect that does
       * not depend on it is a stale closure waiting to be believed, and what this actually needs
       * is "have we already counted this", which is a fact about the component rather than about
       * the answer.
       */
      if (next.status === 'signed-in' && !countedSignIn.current) {
        countedSignIn.current = true;
        count({ kind: 'funnel', event: 'signed-in' });
      }
      setState(next);
    };

    void load();

    return () => {
      activity.cancelled = true;
    };
  }, [reloadToken]);

  useEffect(() => {
    const changed = (event: StorageEvent): void => {
      if (event.key === 'orbit-mind.active-account') {
        setState({ status: 'loading' });
        refresh();
      }
    };
    window.addEventListener('storage', changed);
    return () => {
      window.removeEventListener('storage', changed);
    };
  }, [refresh]);

  useEffect(() => {
    if (state.status !== 'signed-in' || state.user.firebasePassword !== true) return;
    const resume = (): void => { if (document.visibilityState === 'visible') refresh(); };
    const timer = window.setInterval(resume, 120_000);
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('online', resume);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', resume); window.removeEventListener('online', resume); };
  }, [state.status === 'signed-in' ? state.user.firebasePassword : false, refresh]);

  const endSession = useCallback(
    (scope: 'this-device' | 'everywhere') => {
      const run = async (): Promise<void> => {
        setState({ status: 'loading' });
        clearOwnAi();
        await signOut(scope);
        // Re-read rather than assume: a sign-out that did not reach the Worker leaves the session
        // alive, and a screen that had already drawn itself signed out would be lying about it.
        refresh();
      };
      void run();
    },
    [refresh],
  );

  return { state, endSession, refresh };
}
